import moment from 'moment';
import { useState, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useFilters, usePagination, useTable } from 'react-table';
import { useAppContext } from '../../../appProvider/contextProvider';
import Calendars from '../../../components/Calendars/Calendars';
import { getCsvReport, getReportsByDate } from '../../../services/campaignService';

import NoDataImage from '../../../assets/images/illustration.svg';
import { useHistory } from 'react-router';
import { getOverAllStatsByDate } from '../../../services/overAllStats';

import { saveAs } from 'file-saver';
import roundOff, { wholeNumber } from '../../../Util/roundOff';
import HelperComponent from '../../../components/HelperComponent/HelperComponent';
import ReactTooltip from 'react-tooltip';


const CopyCell = ({id}) => {

    const history = useHistory();

    const onCopy = () => {
        history.push('/create-campaign', {
            id: id,
        })
    }

    return <div className='d-flex justify-content-end'>
        <i onClick={onCopy} className={'icon icon-copy'} data-tip={'Copy and send new campaign, Note: Not all the details are copied'}></i>
        <ReactTooltip />
    </div>
}


const columns =  [
    {
        Header: 'Campaign Name',
        accessor: 'title', // accessor is the "key" in the data
    },
    {
        Header: 'Date',
        accessor: 'created_at', // accessor is the "key" in the data
        Cell: (props) => moment(props.value).format('MMM DD, YYYY'),
    },
    {
        Header: 'Status',
        accessor: 'status', // accessor is the "key" in the data
        // Cell: (props) => moment(props.value).format('MMM DD, YYYY'),
    },
    {
        Header: 'Segment',
        accessor: 'segment_name', // accessor is the "key" in the data
        Cell: (props) => props.value ? `${props.value}` : 'All Subscribers',
    },
    {
        Header:() => <div style={{textAlign: 'right'}} >Subscriber</div>,
        accessor: 'sent', // accessor is the "key" in the data
        Cell: (props) => <div style={{textAlign: 'right'}} >{wholeNumber(props.value)}</div>,
    },
    {
        Header:<div style={{textAlign: 'right'}} >Impression</div>,
        accessor: 'delivered', // accessor is the "key" in the data
        Cell: (props) => <div style={{textAlign: 'right'}} >{wholeNumber(props.value)}</div>,
    },
    {
        Header: <div style={{textAlign: 'right'}} >Clicked</div>,
        accessor: 'opened', // accessor is the "key" in the data
        Cell: (props) => <div style={{textAlign: 'right'}} >{wholeNumber(props.value)}</div>,
    },
    {
        Header: <div style={{textAlign: 'right'}} >CTR</div>,
        accessor: 'ctr', // accessor is the "key" in the data
        Cell: (props) => <div style={{textAlign: 'right'}} >{parseFloat(props.value).toFixed(2)}%</div>,
    },
    {
        Header: <div style={{textAlign: 'right'}} >Copy</div>,
        accessor: 'id',
        Cell: ({value}) => <CopyCell id={value} />
    }
]

const CampaignReport = () => {

    const history = useHistory();
    
    const { selectedWebsite, isMutating, isFetching } = useAppContext();


    const [reports, setReports] = useState([]);


    const [isFilterOpen , setIsFilterOpen] = useState(false);

    const [searchString , setSearchString] = useState('');

    const [ noOfRecords, setNoOfRecords ] = useState(0);

    const [subscribersFilter, setSubscribersFilter] = useState({
        name: 'Last 7 Days',
        startDay: moment().subtract(7, 'days').startOf('day'),
        endDay: moment(),
    });

    const [scheduleStats, setScheduleStats] = useState({});

    const records = useMemo(() => {
        if(searchString) {
            const search = new RegExp(`${searchString}`, 'igm');
            return reports.filter(({title}) => title.match(search)).length;
        }
        return noOfRecords;
    }, [searchString, noOfRecords])

    useQuery(['fetchReports', {
        webId: selectedWebsite?.id,
        start_date: subscribersFilter.startDay,
        end_date: subscribersFilter.endDay,
    }], () => getReportsByDate({
        webId: selectedWebsite?.id,
        start_date: subscribersFilter.startDay,
        end_date: subscribersFilter.endDay,
    }), {
        enabled: !!selectedWebsite,
        onSuccess: (campaignsReport) => {
            if(campaignsReport.errors) {

            } else {
                setReports(campaignsReport);
                setNoOfRecords(campaignsReport.length);
            }
        }
    });

    useQuery(
        ['fetchDripNotificationStats', {
            webId: selectedWebsite?.id,
            start_date: subscribersFilter.startDay,
            end_date: subscribersFilter.endDay,
        }], 
        () => getOverAllStatsByDate({
            webId: selectedWebsite?.id,
            start_date: subscribersFilter.startDay,
            end_date: subscribersFilter.endDay,
        }),
        {
            enabled: !!selectedWebsite,
            onSuccess: (stats) => {
                setScheduleStats(stats.campaign);
            }
        }
    )

    
    const reportsData = useTable({
        columns,
        data: reports,
        defaultColumn: columns,
    }, useFilters, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows
    } = reportsData;
      


    const getCustomRange = () => {
        return `${moment(subscribersFilter.startDay).format('DD/MM/YYYY')} - ${moment(subscribersFilter.endDay).format('DD/MM/YYYY')}`;
    }


    const handelSearchChange = (value) => {
        setSearchString(value);
    }

    const getSearchedString = (cell, row) => {
        const search = new RegExp(`${searchString}`, 'i');
        if(searchString.length > 0 && row.original.title.match(search)) {
            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
        } else if(searchString.length<= 0) {
            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
            
        }

    }

    const getCsv = useMutation((event) => {
        event.preventDefault();
        return getCsvReport(selectedWebsite?.id, 
            {
                start_date: subscribersFilter.startDay,
                end_date: subscribersFilter.endDay
            });
    }, {
        onSuccess: (res) => {
            if(res.errors && res.error) {

            } else {
                saveAs(res, 'Report.csv');
            }
        }
    });


    return <>
    <div className="panel full-height">
        <div className="panel-header">
            <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">
                    Report 
                    <HelperComponent 
                        component={'Report'} 
                        href="https://chirpyweb.com/documentation_posts/campaign-reports/" />
                    </h3>
            </div>                                
        </div>
        <div className="panel-body d-flex flex-column full-height scrollable_y">
            <div className="d-flex flex-column full-height scrollable_y">
                <div className="d-flex flex-wrap align-items-start">
                    <div className="col-12 col-md-4 col-lg-4">
                        <div className="form-group">
                            <div className="input-addon input-group-right">
                                <i className="icon icon-search"></i>
                                <input 
                                    type="search" 
                                    placeholder="Search by Campaign name" 
                                    className="form-control"
                                    onChange={({target}) => handelSearchChange(target.value)}
                                />                        
                            </div>  
                        </div>
                    </div>
                    <div className="col-12 col-md-4 col-lg-3">
                        <div className="form-group date-form-group mb-0" >  
                            {/* <input type="date" className="form-control" /> */}
                            <div className="input-addon input-group-right" onClick={() => setIsFilterOpen(!isFilterOpen)} >
                                <i className="icon icon-calendar"></i>
                                <input
                                    value={subscribersFilter.name !== 'Custom Dates' ? subscribersFilter.name : getCustomRange() }
                                    placeholder="This Week" 
                                    className="form-control"
                                />
                            </div>
                            {
                                isFilterOpen && <Calendars
                                    subscribersFilter={subscribersFilter}
                                    setSubscribersFilter={setSubscribersFilter}
                                    setIsFilterOpen={setIsFilterOpen}
                                    left
                                />
                            }
                        </div>                                        
                    </div>
                    <div className="col text-right">
                        <button className="btn btn-primary my-2" onClick={getCsv.mutate}><i className="icon icon-file mr-2"></i>Export CSV</button>
                    </div>                                
                </div>
                <div className="table-responsive campaigns-report-table br-0 full-height">
                    <table className="table table-hover br-0" {...getTableProps()}>
                        <thead>
                            <tr>
                                <th colspan="4"></th>
                                <th style={{textAlign: 'right'}}>
                                    <p className="thin">Total</p>
                                    <h5>{roundOff(scheduleStats?.sent)}</h5>
                                </th>
                                <th style={{textAlign: 'right'}}>
                                    <p className="thin">Total</p>
                                    <h5>{roundOff(scheduleStats?.delivered)}</h5>
                                </th>
                                <th style={{textAlign: 'right'}}>
                                    <p className="thin">Total</p>
                                    <h5>{roundOff(scheduleStats?.opened)}</h5>
                                </th>
                                <th style={{textAlign: 'right'}}>
                                    <p className="thin">Average</p>
                                    <h5>{scheduleStats?.ctr} %</h5>
                                </th>
                            </tr>
                            {
                                headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {
                                            headerGroup.headers.map(column => (
                                                <th {...column.getHeaderProps()}>
                                                    {column.render('Header')}
                                                </th>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </thead>
                        <tbody  {...getTableBodyProps()}>
                        {   // Loop over the table rows
                            rows.map(row => {
                                // Prepare the row for display
                                prepareRow(row)
                                return (
                                // Apply the row props
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map(cell => getSearchedString(cell, row))}
                                    </tr>
                                )
                            })
                        }
                        {
                            (!isFetching && !isMutating && reports.length <= 0) &&  <tr>
                            <td colSpan="8" align="center">
                                    <div className="d-flex flex-column align-items-center justify-content-center mt-5">
                                        <img src={NoDataImage} className="illustration-image" />
                                        <h5 className="medium text-dark mt-2">No campaign sent, please try sending one</h5>
                                        <button className="btn btn-primary my-2" onClick={() => history.push('/create-campaign')}>Create Campaign</button>
                                    </div>                                                    
                                </td>
                            </tr>
                        }
                        </tbody>
                    </table>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <p className="mb-1">Total {records} entries</p>
                    {/* <div className="mb-1">
                        <button className="btn mr-2" disabled={!canPreviousPage}  onClick={() => previousPage()}><i className="icon icon-chevron-left mr-2"></i>Prev</button>
                        <button className="btn " disabled={!canNextPage} onClick={() => nextPage()}>Next <i className="icon icon-chevron-right ml-2"></i></button>
                    </div> */}
                </div>   
            </div>                                                       
        </div>
    </div>


    </>
};


export default CampaignReport;